import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ReactComponent as SvgPlayIcon } from '@img/icons/play-icon.svg';
import { ReactComponent as SvgMirrorOuter } from '@img/masks/mirror-outer.svg';
import { ReactComponent as SvgMirrorPortraitOuter } from '@img/masks/mirror-portrait-outer.svg';
import { ReactComponent as SvgFrameOuter } from '@img/masks/frame-outer.svg';
import { ReactComponent as SvgCurtainsOuter } from '@img/masks/curtains-outer.svg';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
import RichTextStyles, { contentStyles } from '../RichText/RichText.styles';
const Container = styled(SectionWrapper) `
  --bgColor: ${brand.white};
  --bgColorAccent: ${brand.white};
  --icong: ${brand.sage.sageDark};
  --iconBg: ${brand.white};
  --headingColor: ${brand.sage.sageDark};
  --textColor: ${brand.black};

  ${setCssVar('sectionMargin', '40px')};

  position: relative;
  background: var(--bgColor);
  color: var(--textColor);
  overflow: hidden;

  &[data-bg-color='sage1'] {
    --bgColor: ${brand.sage.sageDark};
    --headingColor: ${brand.white};
    --textColor: ${brand.white};

    &[data-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.homeStepper.white};
      --textColor: ${brand.homeStepper.white};
    }
  }

  &[data-bg-color='sage3'] {
    --bgColor: ${brand.sage.sageLight};
    --headingColor: ${brand.white};
    --textColor: ${brand.white};

    &[data-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.sage.sageDark};
      --textColor: ${brand.sage.sageDark};
    }
  }

  &[data-bg-color='plum3'] {
    --bgColor: ${brand.plum.plumLight};

    &[data-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.plum.plumDark};
      --textColor: ${brand.plum.plumDark};
    }
  }

  &[data-bg-color='white'] {
    --bgColor: ${brand.white};

    &[data-bg-color-accent='homeStepper'] {
      --bgColor: ${brand.homeStepper.white};
    }
  }

  &[data-bg-color='grey'] {
    --bgColor: ${brand.grey.greyLight};

    &[data-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.sage.sageDark};
      --textColor: ${brand.homeStepper.black};
    }
  }

  &[data-bg-color='toast1'] {
    --bgColor: ${brand.toast.toastMedium};
  }

  &[data-bg-color='toast2'] {
    --bgColor: ${brand.toast.toastLight};

    &[data-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.toast.toastBurnt};
      --textColor: ${brand.toast.toastBurnt};
    }
  }

  &[data-bg-color='sunshine1'] {
    --bgColor: ${brand.sunshine.sunshineMedium};
  }

  &[data-bg-color='sunshine2'] {
    --bgColor: ${brand.sunshine.sunshineLight};
  }

  &[data-bg-color-accent='sage'] {
    --bgColorAccent: ${brand.sage.sageDark};
  }

  &[data-bg-color-accent='toast'] {
    --bgColorAccent: ${brand.toast.toastMedium};
  }

  &[data-bg-color-accent='plum'] {
    --bgColorAccent: ${brand.plum.plumLight};
  }

  @media ${from(Device.TabletLarge)} {
    ${setCssVar('sectionMargin', '156px')};
  }

  &[data-theme='${1 /* HomeStepper */}'] {
    ${setCssVar('sectionMargin', '75px')};

    ${() => Image} {
      border-radius: 0 0 100px 0;
    }

    ${() => Title} {
      color: var(--headingColor);
      ${fonts.matter.medium};
    }

    ${() => Content} {
      p {
        color: var(--textColor);
        ${fonts.matter.medium};
      }
    }
  }
`;
const Inner = styled.div `
  display: grid;
  align-items: center;
  gap: 40px;
  grid-template-columns: repeat(12, 1fr);
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 1s ${animation.secondary}, transform 1s ${animation.sharp};
  transition-delay: 0.3s;

  @media (prefers-reduced-motion: reduce) {
    transform: translateY(0);
    transition: opacity 1s ${animation.secondary};
  }

  &[data-is-active='true'] {
    opacity: 1;
    transform: translateY(0);
  }

  @media ${until(Device.TabletLarge)} {
    gap: 36px 10px;
  }
`;
const BackgroundAccent = styled.div `
  position: relative;
  display: block;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    display: block;
    background: var(--bgColorAccent);
  }

  [data-layout='title-left-content-right'] & {
    display: none;
  }

  [data-layout='text-only'] & {
    display: none;
  }

  @media ${until(Device.TabletLarge)} {
    grid-row: 1;
    grid-column: 1 / span 12;
    height: 100%;

    &::before {
      content: '';
      top: calc(${cssVar('sectionMargin')} * -1 - 40%);
      left: calc(${cssVar('sitePadding')} * -1);
      height: calc(100% + ${cssVar('sectionMargin')});
      width: 100vw;
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 1;
    height: 100%;

    &::before {
      content: '';
      top: calc(${cssVar('sectionMargin')} * -1);
      height: calc(100% + ${cssVar('sectionMargin')} + ${cssVar('sectionMargin')});
      width: 100vw;
    }

    [data-layout='image-left'] & {
      grid-column: 1 / span 6;

      &::before {
        right: 33%;
      }
    }

    [data-layout='image-right'] & {
      grid-column: -7 / span 6;

      &::before {
        left: 33%;
      }
    }
  }
`;
const ImageWrapper = styled.div `
  position: relative;
  z-index: 2;

  [data-layout='title-left-content-right'] & {
    display: none;
  }

  [data-layout='text-only'] & {
    display: none;
  }

  @media ${until(Device.TabletLarge)} {
    grid-row: 1;
    grid-column: 1 / span 12;
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 1;

    [data-layout='image-left'] & {
      grid-column: 1 / span 6;
    }

    [data-layout='image-right'] & {
      grid-column: -7 / span 6;
    }
  }
`;
const Image = styled.img `
  display: block;
  height: auto;
  object-fit: cover;
  object-position: center;
  width: 100%;

  max-height: 250px;
  @media ${from(Device.Tablet)} {
    max-height: 400px;
  }

  @media ${from(Device.TabletLarge)} {
    max-height: 800px;
  }
`;
const LottieWrapper = styled.div `
  position: relative;
  grid-row: 1;
  z-index: 2;

  [data-layout='text-only'] & {
    display: none;
  }

  @media ${until(Device.TabletLarge)} {
    grid-row: 1;
    grid-column: 1 / span 12;
    justify-self: center;
    max-width: 320px;
  }

  @media ${from(Device.TabletLarge)} {
    [data-layout='image-left'] & {
      grid-column: 1 / span 6;
    }

    [data-layout='image-right'] & {
      grid-column: -7 / span 6;
    }
  }
`;
const VideoWrapper = styled.div `
  position: relative;
  grid-row: 1;
  padding-bottom: ${(574 / 860) * 100}%;
  z-index: 2;

  [data-layout='text-only'] & {
    display: none;
  }

  @media ${until(Device.TabletLarge)} {
    grid-row: 1;
    grid-column: 1 / span 12;
  }

  @media ${from(Device.TabletLarge)} {
    [data-layout='image-left'] & {
      grid-column: 1 / span 6;
    }

    [data-layout='image-right'] & {
      grid-column: -7 / span 6;
    }
  }
`;
const PlayButton = styled.button `
  --iconFg: ${brand.sage.sageDark};
  --iconBg: ${brand.white};

  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  background: none;
  border: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  place-content: center;
  place-items: center;
  width: 100%;

  &:focus,
  &:hover {
    --iconBg: ${brand.sage.sageDark};
    --iconFg: ${brand.white};
  }
`;
const PlayIcon = styled(SvgPlayIcon) `
  display: block;
  height: 75px;
  transform: scale(1);
  transition: transform 0.15s ${animation.sharp};
  width: 75px;

  circle,
  path {
    transition: fill 0.15s ${animation.sharp}, stroke 0.15s ${animation.sharp};
  }

  ${PlayButton}:focus &,
  ${PlayButton}:hover & {
    transform: scale(1.2);
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  grid-row: 1;
  max-width: 560px;
  z-index: 2;

  [data-layout='title-left-content-right'] & {
    @media ${until(Device.Tablet)} {
      align-items: flex-start;
    }
    @media ${from(Device.Tablet)} {
      display: grid;
      grid-template-columns: inherit;
    }
    max-width: unset;
    grid-column: 1 / -1;
  }

  [data-layout='text-only'] & {
    max-width: unset;
  }

  @media ${until(Device.Tablet)} {
    grid-area: 2 / 2 / auto / span 10;
  }

  @media ${until(Device.TabletLarge)} {
    grid-row: 2;
    grid-column: 1 / span 12;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    [data-layout='text-only'] & {
      grid-row: 1;
      justify-self: center;
    }
  }

  @media ${from(Device.TabletLarge)} {
    [data-layout='text-only'] & {
      grid-column: 3 / span 8;
      justify-self: center;
    }

    [data-layout='image-left'] & {
      grid-column: -6 / span 5;
    }

    [data-layout='image-right'] & {
      grid-column: 1 / span 5;
    }
  }
`;
const Tag = styled.small `
  display: block;
  ${fonts.matter.regular};
  font-size: 16px;
  letter-spacing: 0.08em;
  line-height: 24px;
  margin: 0 0 10px;
  text-transform: uppercase;

  @media ${from(Device.TabletLarge)} {
    margin: 0 0 36px;
  }
`;
const Title = styled.h2 `
  ${HeadingStyles.H2Styles}
  color: ${brand.sage.sageDark};
  margin: 0 0 16px;

  [data-layout='title-left-content-right'] & {
    grid-column: 3;
  }

  @media ${from(Device.TabletLarge)} {
    margin: 0 0 32px;
  }

  &[data-has-chat='true'] {
    text-align: left;
  }
`;
const Content = styled.div `
  ${contentStyles}
  p {
    ${fonts.matter.regular};
    font-size: 16px;
    line-height: 24px;
    margin: 0 0 16px;
  }

  [data-layout='title-left-content-right'] & {
    grid-column: 5 / 11;
  }

  a {
    ${RichTextStyles.linkStyles}
    ${setCssVar('fgColor', brand.black)};
    ${setCssVar('bgColor', brand.sage.sageLight)};
  }

  @media ${from(Device.TabletLarge)} {
    p {
      font-size: 18px;
      line-height: 28px;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    p {
      font-size: 20px;
      line-height: 30px;
    }
  }

  @media ${from(Device.Desktop)} {
    p {
      font-size: 22px;
      line-height: 32px;
    }
  }
`;
const CtaWrapper = styled.div `
  margin: 32px 0 0;

  [data-layout='title-left-content-right'] & {
    @media ${from(Device.DesktopLarge)} {
      margin: 0;
    }
    grid-column: 5 / 11;
    grid-row: 2;
  }
`;
const MirrorOuter = styled(SvgMirrorOuter) `
  position: absolute;
  height: 100%;
  left: 50%;
  transform: translateX(-50%);
  color: ${brand.sage.sageDark};
`;
const MirrorPortraitOuter = styled(SvgMirrorPortraitOuter) `
  position: absolute;
  height: 100%;
  left: 50%;
  transform: translateX(-50%);
  color: ${brand.sage.sageDark};
`;
const CurtainOuter = styled(SvgCurtainsOuter) `
  position: absolute;
  height: 100%;
  left: 50%;
  transform: translateX(-50%);
  color: ${brand.sage.sageDark};
`;
const FrameOuter = styled(SvgFrameOuter) `
  position: absolute;
  height: 80%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: ${brand.sage.sageDark};
`;
const SplitContentStyles = {
    BackgroundAccent,
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    CurtainOuter,
    FrameOuter,
    Image,
    ImageWrapper,
    Inner,
    LottieWrapper,
    MirrorOuter,
    MirrorPortraitOuter,
    PlayButton,
    PlayIcon,
    Tag,
    Title,
    VideoWrapper,
};
export default SplitContentStyles;
